import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminUploadService } from '../../services/adminUploadService';
import { 
  GRADE_LIST, FIELD_LIST, getSubjectsFor, needsField as curriculumNeedsField 
} from '../../core/education/curriculum';
import { JOZVE_DATA } from '../../data/jozveData';
import './AdminUploadPDF.css';

const getApiBase = () => {
  const hostname = window.location.hostname;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1" || /^192\.168\./.test(hostname) || /^10\./.test(hostname);
  if (isLocal) return `http://${hostname}:3001`;
  return process.env.REACT_APP_API_URL || '/api';
};

const FILE_BASE = process.env.REACT_APP_FILE_API_URL || 'http://localhost:3002';

export default function AdminUploadPDF() {
  const navigate = useNavigate();
  
  // ===== State =====
  const [activeTab, setActiveTab] = useState('textbook-pdf');
  const [expandedGrades, setExpandedGrades] = useState({});
  const [adminUploads, setAdminUploads] = useState([]);
  const [deletedKeys, setDeletedKeys] = useState(new Set()); // برای مخفی کردن فایل‌های استاتیک حذف شده
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  // ===== فرم آپلود =====
  const [form, setForm] = useState({
    grade: '1', field: '', subject: '', title: '', bookId: '',
    chapterNumber: '1', lessonNumber: '1', coinPrice: '500', tomanPrice: '50000', isPremium: false
  });
  const [file, setFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  const needsField = curriculumNeedsField(form.grade);
  const subjectsList = useMemo(() => {
    if (needsField && !form.field) return [];
    return getSubjectsFor(form.grade, needsField ? form.field : null);
  }, [form.grade, form.field, needsField]);

  // ===== دریافت داده‌ها =====
  const loadAdminUploads = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBase()}/adminUploads`);
      if (res.ok) {
        const data = await res.json();
setAdminUploads(Array.isArray(data) ? data : []);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { loadAdminUploads(); }, [loadAdminUploads]);

  // ===== محاسبه ساختار درختی (ترکیب استاتیک و داینامیک) =====
const treeData = useMemo(() => {
  const tree = {}; // { grade: { field: { subject: [items] } } }

  GRADE_LIST.forEach(g => {
    const gTree = {};
    const fields = curriculumNeedsField(g.id) ? FIELD_LIST : [{ id: null, title: 'عمومی' }];
    
    fields.forEach(f => {
      const subjects = getSubjectsFor(g.id, f.id);
      const sTree = {};
      subjects.forEach(s => {
        const items = [];

        // فقط فایل‌های داینامیک (آپلود شده توسط ادمین)
        adminUploads.filter(u => u.category === activeTab && String(u.grade) === String(g.id) && (u.field || null) === f.id && u.subject === s.id && !u.isDeleted)
          .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
          .forEach(u => {
            items.push({
              id: u.id, title: u.title, isStatic: false, bookId: u.bookId,
              fileUrl: u.fileUrl, coverImage: u.coverImage, sortIndex: u.sortIndex
            });
          });

        if (items.length > 0) sTree[s.id] = items;
      });
      if (Object.keys(sTree).length > 0) gTree[f.id] = sTree;
    });
    if (Object.keys(gTree).length > 0) tree[g.id] = gTree;
  });
  return tree;
}, [activeTab, adminUploads]);

  // ===== هندلرهای آپلود =====
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!file || !form.subject) return setMessage({ type: 'error', text: 'فایل و درس الزامی است.' });
    setUploading(true); setMessage(null);
    try {
const res = await adminUploadService.uploadPdf({
  file, coverFile, category: activeTab, grade: form.grade, field: form.field,
  subject: form.subject, bookId: form.bookId
});
const metadata = {
  category: activeTab, grade: form.grade, field: form.field, subject: form.subject,
  title: form.title || subjectsList.find(s => s.id === form.subject)?.title || 'بدون نام',
  bookId: res.bookId, fileUrl: res.fileUrl, coverImage: res.coverUrl,
  chapterNumber: (activeTab === 'gama' || activeTab === 'exam-pdf') ? Number(form.chapterNumber) : null,
  lessonNumber: (activeTab === 'gama' || activeTab === 'exam-pdf') ? Number(form.lessonNumber) : null,
  coinPrice: (activeTab === 'gama' || activeTab === 'exam-pdf') ? (form.isPremium ? Number(form.coinPrice) : 0) : Number(form.coinPrice),
  tomanPrice: activeTab === 'workbook-pdf' ? Number(form.tomanPrice) : 0,
  isPremium: (activeTab === 'gama' || activeTab === 'exam-pdf') ? form.isPremium : false,
  sortIndex: Date.now(),
  createdAt: new Date().toISOString()
};

// 🆕 Rollback: اگر ثبت متادیتا fail شد، فایل فیزیکی را پاک کن
try {
  const metaRes = await fetch(`${getApiBase()}/adminUploads`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(metadata)
  });
  if (!metaRes.ok) throw new Error(`ثبت متادیتا ناموفق بود (HTTP ${metaRes.status})`);
} catch (metaErr) {
  // فایل روی دیسک هست ولی رکوردی در DB نیست → فوراً پاکش کن
  try {
    await adminUploadService.deletePhysicalFiles({
      fileUrl: res.fileUrl, coverUrl: res.coverUrl
    });
  } catch (cleanupErr) {
    console.error('⚠️ rollback حذف فیزیکی هم fail شد:', cleanupErr);
  }
  throw metaErr;
}
      setMessage({ type: 'success', text: '✅ با موفقیت آپلود شد!' });
      setFile(null); setCoverFile(null); setForm(f => ({ ...f, title: '', bookId: '' }));
      document.getElementById('pdf-upload-input').value = '';
      document.getElementById('cover-upload-input').value = '';
      loadAdminUploads();
    } catch (error) { setMessage({ type: 'error', text: error.message }); }
    finally { setUploading(false); }
  };

  // ===== هندلرهای مدیریت (حذف و جابجایی) =====
  const handleDelete = async (item) => {
    if (!window.confirm("آیا از حذف کامل این فایل مطمئن هستید؟ این عمل غیرقابل بازگشت است.")) return;
    try {
      if (item.fileUrl) await adminUploadService.deletePhysicalFiles({ fileUrl: item.fileUrl, coverUrl: item.coverImage });
      
      if (item.isStatic) {
        // ثبت مخفی‌سازی برای فایل استاتیک
        await fetch(`${getApiBase()}/adminUploads`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bookId: item.id, category: activeTab, isDeleted: true })
        });
      } else {
        // حذف رکورد داینامیک
        await fetch(`${getApiBase()}/adminUploads/${item.id}`, { method: 'DELETE' });
      }
      loadAdminUploads();
      alert("✅ فایل با موفقیت حذف شد.");
    } catch (e) { alert("خطا در حذف: " + e.message); }
  };

  const handleMove = async (item, direction, listItems) => {
    if (item.isStatic) return alert("فایل‌های استاتیک قدیمی قابل جابجایی نیستند.");
    const idx = listItems.findIndex(i => i.id === item.id);
    if (direction === 'up' && idx > 0) {
      const prev = listItems[idx - 1];
      if (prev.isStatic) return alert("نمی‌توانید فایل جدید را بالاتر از فایل استاتیک ببرید.");
      const newIdx = item.sortIndex;
      await fetch(`${getApiBase()}/adminUploads/${item.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sortIndex: prev.sortIndex }) });
      await fetch(`${getApiBase()}/adminUploads/${prev.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sortIndex: newIdx }) });
      loadAdminUploads();
    } else if (direction === 'down' && idx < listItems.length - 1) {
      const next = listItems[idx + 1];
      const newIdx = item.sortIndex;
      await fetch(`${getApiBase()}/adminUploads/${item.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sortIndex: next.sortIndex || newIdx + 1 }) });
      if (!next.isStatic) await fetch(`${getApiBase()}/adminUploads/${next.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sortIndex: newIdx }) });
      loadAdminUploads();
    }
  };

  return (
    <div className="admin-upload-page" dir="rtl">
      <button className="back-btn" onClick={() => navigate('/admin/books')}>← بازگشت به پنل مدیریت</button>

      <div className="admin-upload-header">
        <h1>👑 ابر پنل مدیریت فایل‌های آموزشی</h1>
        <p>مدیریت، حذف و جابجایی تمام فایل‌های کتاب درسی، کتاب کار و جزوه‌ها</p>
      </div>

      {/* تب‌های انتخاب دسته */}
      <div className="admin-tabs">
        {[
          { id: 'textbook-pdf', label: '📖 کتاب درسی', color: '#0984E3' },
          { id: 'workbook-pdf', label: '✏️ کتاب کار', color: '#fdea6e' },
          { id: 'gama', label: '✨ جزوه درسی', color: '#6C5CE7' },
          { id: 'exam-pdf', label: '📝 نمونه سوالات', color: '#10B981' }
        ].map(tab => (
          <button key={tab.id} className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} 
            style={{ '--tab-color': tab.color }} 
            onClick={() => {
              setActiveTab(tab.id);
              setFile(null);
              setCoverFile(null);
              // 🆕 ریست کردن قیمت‌ها هنگام تغییر تب
              setForm(f => ({ 
                ...f, 
                title: '', 
                bookId: '', 
                coinPrice: tab.id === 'workbook-pdf' ? '500' : '100', 
                tomanPrice: '50000', 
                isPremium: false 
              }));
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* بخش آپلود */}
      <div className="admin-upload-card">
        <h3>📤 آپلود فایل جدید برای {activeTab === 'textbook-pdf' ? 'کتاب درسی' : activeTab === 'workbook-pdf' ? 'کتاب کار' : activeTab === 'exam-pdf' ? 'نمونه سوال' : 'جزوه'}</h3>
        <form onSubmit={handleUploadSubmit} className="admin-upload-form">
          <div className="form-row">
            <div className="form-group">
              <label>پایه</label>
              <select value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value, field: '', subject: '' }))}>
                {GRADE_LIST.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
              </select>
            </div>
            {needsField && (
              <div className="form-group">
                <label>رشته</label>
                <select value={form.field} onChange={e => setForm(f => ({ ...f, field: e.target.value, subject: '' }))}>
                  <option value="">-- انتخاب --</option>
                  {FIELD_LIST.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>درس</label>
              <select value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} disabled={needsField && !form.field}>
                <option value="">-- انتخاب --</option>
                {subjectsList.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>نام نمایشی</label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="نام روی کارت..." />
            </div>
            {activeTab === 'workbook-pdf' && (
              <>
                <div className="form-group">
                  <label>قیمت سکه</label>
                  <input 
                    type="text" 
                    inputMode="numeric" 
                    pattern="[0-9]*" 
                    value={form.coinPrice} 
                    onChange={e => {
                      const val = e.target.value
                        .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                        .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                        .replace(/[^0-9]/g, '');
                      setForm(f => ({ ...f, coinPrice: val }));
                    }} 
                  />
                </div>
                <div className="form-group">
                  <label>قیمت تومان</label>
                  <input 
                    type="text" 
                    inputMode="numeric" 
                    pattern="[0-9]*" 
                    value={form.tomanPrice} 
                    onChange={e => {
                      const val = e.target.value
                        .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                        .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                        .replace(/[^0-9]/g, '');
                      setForm(f => ({ ...f, tomanPrice: val }));
                    }} 
                  />
                </div>
              </>
            )}
            {(activeTab === 'gama' || activeTab === 'exam-pdf') && (
              <>
                <div className="form-group">
                  <label>{activeTab === 'exam-pdf' ? 'شماره سری' : 'شماره فصل'}</label>
                  <input 
                    type="text" 
                    inputMode="numeric" 
                    pattern="[0-9]*" 
                    value={form.chapterNumber} 
                    onChange={e => {
                      const val = e.target.value
                        .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                        .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                        .replace(/[^0-9]/g, '');
                      setForm(f => ({ ...f, chapterNumber: val }));
                    }} 
                  />
                </div>
                <div className="form-group">
                  <label>{activeTab === 'exam-pdf' ? 'شماره سوال' : 'شماره درس'}</label>
                  <input 
                    type="text" 
                    inputMode="numeric" 
                    pattern="[0-9]*" 
                    value={form.lessonNumber} 
                    onChange={e => {
                      const val = e.target.value
                        .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                        .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                        .replace(/[^0-9]/g, '');
                      setForm(f => ({ ...f, lessonNumber: val }));
                    }} 
                  />
                </div>
                <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <input type="checkbox" checked={form.isPremium} onChange={e => setForm(f => ({ ...f, isPremium: e.target.checked }))} id="prem" />
                  <label htmlFor="prem">ویژه</label>
                </div>
                {form.isPremium && (
                  <div className="form-group">
                    <label>قیمت سکه ویژه</label>
                    <input 
                      type="text" 
                      inputMode="numeric" 
                      pattern="[0-9]*" 
                      value={form.coinPrice} 
                      onChange={e => {
                        const val = e.target.value
                          .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                          .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                          .replace(/[^0-9]/g, '');
                        setForm(f => ({ ...f, coinPrice: val }));
                      }} 
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="form-row">
            <div className="form-group file-upload-group"><input id="pdf-upload-input" type="file" accept=".pdf" onChange={e => setFile(e.target.files[0])} /><label htmlFor="pdf-upload-input">{file ? `📄 ${file.name}` : 'انتخاب PDF'}</label></div>
            <div className="form-group file-upload-group"><input id="cover-upload-input" type="file" accept="image/*" onChange={e => setCoverFile(e.target.files[0])} /><label htmlFor="cover-upload-input">{coverFile ? `🖼️ ${coverFile.name}` : 'انتخاب کاور'}</label></div>
          </div>

          {message && <div className={`alert-box ${message.type}`}>{message.text}</div>}
          <button type="submit" className="submit-btn" disabled={uploading}>{uploading ? '⏳...' : '🚀 آپلود'}</button>
        </form>
      </div>

      {/* لیست فایل‌های موجود */}
      <div className="admin-files-list">
        <h3>📂 فایل‌های موجود در این دسته</h3>
        {Object.keys(treeData).length === 0 ? <p className="no-data">هیچ فایلی یافت نشد.</p> : (
          Object.entries(treeData).map(([gId, fields]) => (
            <div key={gId} className="admin-grade-box">
              <div className="admin-grade-header" onClick={() => setExpandedGrades(p => ({ ...p, [gId]: !p[gId] }))}>
                <span>{GRADE_LIST.find(g => g.id === gId)?.title}</span>
                <span>{expandedGrades[gId] ? '▲' : '▼'}</span>
              </div>
              {expandedGrades[gId] && (
                <div className="admin-grade-content">
                  {Object.entries(fields).map(([fId, subjects]) => (
                    <div key={fId} className="admin-field-group">
                      {fId !== 'null' && <h5 className="admin-field-title">{FIELD_LIST.find(f => f.id === fId)?.shortTitle}</h5>}
                      {Object.entries(subjects).map(([sId, items]) => (
                        <div key={sId} className="admin-subject-group">
                          <h6 className="admin-subject-title">{items[0].title.split(' ')[0]}</h6>
                          <div className="admin-items-grid">
                            {items.map(item => (
                              <div key={item.id} className="admin-item-card">
                                {item.coverImage ? <img src={`${FILE_BASE}${item.coverImage}`} alt="" className="admin-item-cover" /> : <div className="admin-item-cover placeholder">📄</div>}
                                <div className="admin-item-info">
                                  <span className="admin-item-title">{item.title}</span>
                                  {item.isStatic && <span className="admin-item-badge">استاتیک</span>}
                                </div>
                                <div className="admin-item-actions">
                                  {!item.isStatic && (
                                    <>
                                      <button onClick={() => handleMove(item, 'up', items)} title="بالا">⬆️</button>
                                      <button onClick={() => handleMove(item, 'down', items)} title="پایین">⬇️</button>
                                    </>
                                  )}
                                  <button className="delete-btn" onClick={() => handleDelete(item)} title="حذف کامل">🗑️</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}